import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { signIn } from '../containers/App/actions';
import { selectUser } from '../containers/App/selectors';
import logo from './assets/logo.png';
import V2Chart from './components/V2Chart';

const V2Landing: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [emailError, setEmailError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');

  if (user) {
    return <Navigate to="/statistics" replace />;
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const passwordValid = password.length > 0;
    setEmailError(emailValid ? '' : 'Введите корректный email.');
    setPasswordError(passwordValid ? '' : 'Введите пароль.');
    if (!emailValid || !passwordValid) {
      return;
    }

    dispatch(signIn({ email, password }));
  };

  return (
    <div className="v2-theme" data-page="landing">
      <a className="skip-link" href="#main">
        К содержимому
      </a>
      <header className="public-header shell">
        <Link className="brand" to="/" aria-label="Fish Hunter — главная">
          <img src={logo} alt="Fish Hunter" />
        </Link>
        <nav className="public-nav" aria-label="Главная навигация">
          <a href="#features">Возможности</a>
          <Link to="/subscription">Тарифы</Link>
          <Link to="/faq">FAQ</Link>
        </nav>
        <a className="header-login outline-button" href="#login">
          Войти <span aria-hidden="true">↗</span>
        </a>
      </header>

      <main className="landing shell" id="main">
        <div className="hero-grid">
          <section className="hero-copy">
            <p className="eyebrow">
              <span className="status-dot"></span> FISH HUNTER / POKER ANALYTICS
            </p>
            <h1>
              У каждого
              <br />
              игрока есть
              <br />
              <em>почерк.</em>
            </h1>
            <p className="hero-description">
              Статистика, которая раскрывает игру.
              <br className="desktop-break" />
              Замечайте закономерности. Находите свой перевес.
            </p>
            <Link className="text-link hero-link" to="/statistics">
              Открыть статистику <span aria-hidden="true">↗</span>
            </Link>
          </section>

          <section className="login-card" id="login" aria-labelledby="login-title">
            <div className="login-kicker">
              <span className="eyebrow">ВАШЕ РАБОЧЕЕ ПРОСТРАНСТВО</span>
              <span className="large-arrow" aria-hidden="true">
                ↗
              </span>
            </div>
            <h2 id="login-title">С возвращением.</h2>
            <p className="login-subtitle">Войдите в Fish Hunter.</p>

            <form id="login-form" noValidate onSubmit={handleSubmit}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-describedby="email-error"
                aria-invalid={Boolean(emailError)}
              />
              <span id="email-error" className="field-error" aria-live="polite">
                {emailError}
              </span>

              <div className="password-label">
                <label htmlFor="password">Пароль</label>
                <Link to="/reset">Забыли пароль?</Link>
              </div>

              <div className="password-input">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Введите пароль"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-describedby="password-error"
                  aria-invalid={Boolean(passwordError)}
                />
                <button
                  id="show-password"
                  type="button"
                  className="icon-button"
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  <svg className="v2-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <span id="password-error" className="field-error" aria-live="polite">
                {passwordError}
              </span>

              <button className="login-submit" type="submit">
                Войти <span aria-hidden="true">↗</span>
              </button>
            </form>

            <div className="signup-row">
              <span>Впервые здесь?</span>
              <Link to="/register">
                Создать аккаунт <span aria-hidden="true">↗</span>
              </Link>
            </div>
            <a
              className="manager-link"
              href="https://t.me/fishh_support"
              target="_blank"
              rel="noreferrer"
            >
              Помощь с доступом <span aria-hidden="true">↗</span>
            </a>
          </section>

          <section className="profile-preview" aria-label="Пример интерфейса">
            <div className="section-meta">
              <span className="eyebrow">ОДИН ПРОФИЛЬ. ВСЯ КАРТИНА.</span>
              <span>Интерфейс</span>
            </div>
            <div className="profile-preview-body">
              <div>
                <div className="preview-name">
                  Player <span className="room-chip">WPN</span>
                </div>
                <p className="muted">Поиск · фильтры · график</p>
                <strong className="preview-winnings">Fish Hunter</strong>
                <span className="preview-ratio">
                  VPIP / PFR <small>и postflop</small>
                </span>
              </div>
              <div className="mini-chart">
                <V2Chart mini />
              </div>
            </div>
            <Link className="preview-more" to="/statistics">
              Открыть статистику <span aria-hidden="true">↗</span>
            </Link>
          </section>
        </div>

        <section className="features" id="features" aria-label="Возможности Fish Hunter">
          <article>
            <span className="feature-number">01</span>
            <div>
              <h2>Читайте игру</h2>
              <p>Preflop, Postflop и история результатов. От общей картины — к каждой детали.</p>
            </div>
            <span className="feature-icon" aria-hidden="true">
              ↗
            </span>
          </article>
          <article>
            <span className="feature-number">02</span>
            <div>
              <h2>Находите игроков</h2>
              <p>Поиск по нику, рейтинг и автопоиск из буфера обмена через расширение.</p>
            </div>
            <span className="feature-icon" aria-hidden="true">
              ⌕
            </span>
          </article>
          <article>
            <span className="feature-number">03</span>
            <div>
              <h2>Держите фокус</h2>
              <p>Компактный режим рядом со столом. Нужные показатели всегда под рукой.</p>
            </div>
            <span className="feature-icon" aria-hidden="true">
              ⌗
            </span>
          </article>
        </section>

        <div className="rooms-strip">
          <span className="eyebrow">ВАША ИГРА. ВАШ РУМ.</span>
          <div>
            <span>POKERSTARS</span>
            <span>888POKER</span>
            <span>WPN</span>
            <span>CHICO</span>
            <span>COINPOKER</span>
            <span>IPOKER</span>
          </div>
        </div>
      </main>

      <footer className="public-footer shell">
        <span>
          FISH HUNTER <span className="muted">© 2026</span>
        </span>
        <span className="muted">Данные. Контекст. Решения.</span>
        <Link to="/">Fish Hunter ↗</Link>
      </footer>
    </div>
  );
};

export default V2Landing;
